import { useState, useEffect, useCallback } from 'react'
import {
  BarChart3, CheckCircle2, Clock, Zap, Star,
  Users, TrendingUp, TrendingDown, Minus, RefreshCw,
  Loader2, MapPin, AlertTriangle, ThumbsUp, Award, Calendar
} from 'lucide-react'
import { taskService } from '@/services/firebase/tasks'
import { engineerService } from '@/services/firebase/engineers'
import type { Task, TaskStatus, TaskType, TaskPriority } from '@/types'
import type { FieldEngineer } from '@/services/firebase/engineers'

// ── colour maps ───────────────────────────────────────────────────────────────
const STATUS_COLOR: Record<TaskStatus, string> = {
  open:             '#3b82f6',
  assigned:         '#8b5cf6',
  in_progress:      '#6366f1',
  site_visit:       '#06b6d4',
  work_in_progress: '#f97316',
  pending_approval: '#f59e0b',
  pending_feedback: '#a855f7',
  closed:           '#10b981',
  cancelled:        '#9ca3af',
}
const TYPE_COLOR: Record<TaskType, string> = {
  new_connection: '#2563eb',
  support:        '#ef4444',
  enquiry:        '#8b5cf6',
  others:         '#64748b',
}
const PRIORITY_COLOR: Record<TaskPriority, string> = {
  low:    '#6b7280',
  medium: '#3b82f6',
  high:   '#f97316',
  urgent: '#ef4444',
}
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

// ── pure helpers ──────────────────────────────────────────────────────────────
const pct = (n: number, total: number) => total ? Math.round((n / total) * 100) : 0
const avg = (arr: number[]) => arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : 0

// ── sub-components ────────────────────────────────────────────────────────────
const KpiCard = ({ label, value, sub, icon: Icon, grad, trend }: {
  label: string; value: string | number; sub?: string
  icon: React.ElementType; grad: string; trend?: 'up' | 'down' | 'flat'
}) => (
  <div className="bg-white rounded-3xl p-5 flex flex-col gap-3"
    style={{ boxShadow: '0 1px 6px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)' }}>
    <div className="flex items-center justify-between">
      <div className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0"
        style={{ background: grad }}>
        <Icon size={18} className="text-white" />
      </div>
      {trend && (
        <span className={`flex items-center gap-1 text-[10px] font-black px-2 py-1 rounded-full ${
          trend === 'up' ? 'text-emerald-700 bg-emerald-50'
          : trend === 'down' ? 'text-red-600 bg-red-50'
          : 'text-gray-500 bg-gray-50'
        }`}>
          {trend === 'up' ? <TrendingUp size={10} /> : trend === 'down' ? <TrendingDown size={10} /> : <Minus size={10} />}
          {trend === 'up' ? '+' : trend === 'down' ? '-' : ''}
        </span>
      )}
    </div>
    <div>
      <p className="text-2xl font-black text-gray-900 leading-none">{value}</p>
      <p className="text-xs font-bold text-gray-500 mt-1">{label}</p>
      {sub && <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>}
    </div>
  </div>
)

const HBar = ({ label, value, total, color, badge }: {
  label: string; value: number; total: number; color: string; badge?: string
}) => {
  const p = pct(value, total)
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="w-28 shrink-0 text-xs font-semibold text-gray-600 truncate capitalize">{label}</div>
      <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700"
          style={{ width: `${p}%`, background: color }} />
      </div>
      <div className="w-16 text-right shrink-0">
        <span className="text-xs font-black text-gray-700">{value}</span>
        <span className="text-[10px] text-gray-400 ml-1">{p}%</span>
      </div>
      {badge && <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full shrink-0"
        style={{ background: `${color}18`, color }}>{badge}</span>}
    </div>
  )
}

const MiniStars = ({ rating }: { rating: number }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <span key={i} className={`text-xs leading-none ${i < Math.round(rating) ? 'text-amber-400' : 'text-gray-200'}`}>★</span>
    ))}
  </div>
)

const SectionCard = ({ title, subtitle, icon: Icon, iconBg, children, action }: {
  title: string; subtitle?: string; icon: React.ElementType; iconBg: string
  children: React.ReactNode; action?: React.ReactNode
}) => (
  <div className="bg-white rounded-3xl overflow-hidden"
    style={{ boxShadow: '0 1px 6px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)' }}>
    <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${iconBg}`}>
          <Icon size={14} className="text-white" />
        </div>
        <div>
          <p className="text-sm font-black text-gray-800">{title}</p>
          {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
    <div className="px-5 py-4">{children}</div>
  </div>
)

// ── Donut chart (pure SVG) ─────────────────────────────────────────────────────
const Donut = ({ segments, total, label }: {
  segments: { value: number; color: string; name: string }[]
  total: number; label: string
}) => {
  const R = 40, cx = 50, cy = 50
  const circumference = 2 * Math.PI * R
  let offset = 0
  const slices = segments.map(s => {
    const dash = total ? (s.value / total) * circumference : 0
    const gap  = circumference - dash
    const rot  = offset
    offset += dash
    return { ...s, dash, gap, rot }
  })
  return (
    <div className="flex items-center gap-5">
      <svg width="100" height="100" viewBox="0 0 100 100" className="shrink-0 -rotate-90">
        <circle cx={cx} cy={cy} r={R} fill="none" stroke="#f3f4f6" strokeWidth="14" />
        {slices.filter(s => s.dash > 0).map((s, i) => (
          <circle key={i} cx={cx} cy={cy} r={R} fill="none"
            stroke={s.color} strokeWidth="14"
            strokeDasharray={`${s.dash} ${s.gap}`}
            strokeDashoffset={-offset + s.rot - slices.reduce((a, x, ii) => ii < i ? a + x.dash : a, 0)}
            style={{ transform: `rotate(${(slices.slice(0, i).reduce((a, x) => a + x.dash, 0) / circumference) * 360}deg)`, transformOrigin: '50px 50px' }}
          />
        ))}
        <text x="50" y="54" textAnchor="middle" fontSize="13" fontWeight="900"
          fill="#111827" className="rotate-90" style={{ transform: 'rotate(90deg)', transformOrigin: '50px 50px' }}>
          {total}
        </text>
      </svg>
      <div className="flex-1 space-y-1.5">
        <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2">{label}</p>
        {segments.map(s => (
          <div key={s.name} className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: s.color }} />
              <span className="text-xs text-gray-600 capitalize truncate">{s.name.replace(/_/g, ' ')}</span>
            </div>
            <span className="text-xs font-black text-gray-800 shrink-0">{s.value}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Monthly bars (pure CSS) ────────────────────────────────────────────────────
const MonthlyBars = ({ data }: { data: { month: string; created: number; closed: number }[] }) => {
  const maxVal = Math.max(...data.flatMap(d => [d.created, d.closed]), 1)
  return (
    <div className="flex items-end gap-1.5 h-36 pt-4">
      {data.map(d => (
        <div key={d.month} className="flex-1 flex flex-col items-center gap-1">
          <div className="w-full flex items-end gap-0.5 justify-center" style={{ height: '96px' }}>
            <div className="flex-1 rounded-t-md transition-all duration-700 min-h-[2px]"
              style={{ height: `${(d.created / maxVal) * 96}px`, background: 'rgba(99,102,241,0.7)' }}
              title={`Created: ${d.created}`} />
            <div className="flex-1 rounded-t-md transition-all duration-700 min-h-[2px]"
              style={{ height: `${(d.closed / maxVal) * 96}px`, background: 'rgba(16,185,129,0.7)' }}
              title={`Closed: ${d.closed}`} />
          </div>
          <span className="text-[9px] text-gray-400 font-semibold">{d.month}</span>
        </div>
      ))}
    </div>
  )
}

// ── main component ─────────────────────────────────────────────────────────────
interface Analytics {
  tasks: Task[]
  engineers: FieldEngineer[]
  total: number
  byStatus: Record<string, number>
  byType: Record<string, number>
  byPriority: Record<string, number>
  byCity: Record<string, number>
  closureRate: number
  avgRating: number
  recommendPct: number
  ratingCount: number
  engineerStats: { id: string; name: string; total: number; closed: number; avgRating: number; rate: number }[]
  monthly: { month: string; created: number; closed: number }[]
  avgCloseTimeDays: number | null
  pendingApproval: number
  openTasks: number
}

const compute = (tasks: Task[], engineers: FieldEngineer[]): Analytics => {
  const total = tasks.length
  const byStatus: Record<string, number> = {}
  const byType: Record<string, number> = {}
  const byPriority: Record<string, number> = {}
  const byCity: Record<string, number> = {}

  tasks.forEach(t => {
    byStatus[t.status] = (byStatus[t.status] ?? 0) + 1
    byType[t.type] = (byType[t.type] ?? 0) + 1
    byPriority[t.priority] = (byPriority[t.priority] ?? 0) + 1
    const city = t.city?.trim() || 'Unknown'
    byCity[city] = (byCity[city] ?? 0) + 1
  })

  const closed = tasks.filter(t => t.status === 'closed')
  const closureRate = pct(closed.length, total)

  const rated = tasks.filter(t => t.customerRating != null)
  const avgRating = rated.length ? Math.round(avg(rated.map(t => t.customerRating!)) * 10) / 10 : 0
  const recommends = tasks.filter(t => t.wouldRecommend === true).length
  const recommendPct = rated.length ? pct(recommends, rated.length) : 0

  // Close time (days)
  const times = closed
    .filter(t => t.closedAt && t.createdAt)
    .map(t => (t.closedAt!.getTime() - t.createdAt.getTime()) / 86_400_000)
  const avgCloseTimeDays = times.length ? Math.round(avg(times) * 10) / 10 : null

  // Per-engineer stats
  const engMap: Record<string, { name: string; total: number; closed: number; ratings: number[] }> = {}
  tasks.forEach(t => {
    if (!t.assignedTo) return
    if (!engMap[t.assignedTo]) engMap[t.assignedTo] = { name: t.assignedEngineerName ?? 'Unknown', total: 0, closed: 0, ratings: [] }
    engMap[t.assignedTo].total++
    if (t.status === 'closed') engMap[t.assignedTo].closed++
    if (t.customerRating) engMap[t.assignedTo].ratings.push(t.customerRating)
  })
  const engineerStats = Object.entries(engMap)
    .map(([id, e]) => ({ id, name: e.name, total: e.total, closed: e.closed, avgRating: avg(e.ratings), rate: pct(e.closed, e.total) }))
    .sort((a, b) => b.closed - a.closed)

  // Monthly (last 6 months)
  const now = new Date()
  const monthly = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - 5 + i, 1)
    const next = new Date(d.getFullYear(), d.getMonth() + 1, 1)
    const created = tasks.filter(t => t.createdAt >= d && t.createdAt < next).length
    const closedN = tasks.filter(t => t.closedAt && t.closedAt >= d && t.closedAt < next).length
    return { month: MONTHS[d.getMonth()], created, closed: closedN }
  })

  return {
    tasks, engineers, total, byStatus, byType, byPriority, byCity,
    closureRate, avgRating, recommendPct, ratingCount: rated.length,
    engineerStats, monthly, avgCloseTimeDays,
    pendingApproval: byStatus['pending_approval'] ?? 0,
    openTasks: (byStatus['open'] ?? 0) + (byStatus['assigned'] ?? 0),
  }
}

const ReportsPage = () => {
  const [data, setData] = useState<Analytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [range, setRange] = useState<'all' | '30d' | '7d'>('all')

  const load = useCallback(async () => {
    setLoading(true)
    const [tRes, eRes] = await Promise.all([taskService.list(), engineerService.list()])
    const allTasks = tRes.success && tRes.data ? tRes.data : []
    const engineers = eRes.success && eRes.data ? eRes.data : []

    const now = Date.now()
    const filtered = range === '30d' ? allTasks.filter(t => now - t.createdAt.getTime() < 30 * 86_400_000)
      : range === '7d' ? allTasks.filter(t => now - t.createdAt.getTime() < 7 * 86_400_000)
      : allTasks

    setData(compute(filtered, engineers))
    setLoading(false)
  }, [range])

  useEffect(() => { load() }, [load])

  const statusSegments = data ? Object.entries(data.byStatus)
    .map(([k, v]) => ({ name: k, value: v, color: STATUS_COLOR[k as TaskStatus] ?? '#9ca3af' }))
    .sort((a, b) => b.value - a.value) : []

  const typeSegments = data ? Object.entries(data.byType)
    .map(([k, v]) => ({ name: k.replace('_', ' '), value: v, color: TYPE_COLOR[k as TaskType] ?? '#9ca3af' }))
    .sort((a, b) => b.value - a.value) : []

  const topCities = data ? Object.entries(data.byCity)
    .sort(([, a], [, b]) => b - a).slice(0, 8) : []

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-32 gap-3">
      <div className="w-14 h-14 rounded-3xl flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg,rgba(99,102,241,0.12),rgba(139,92,246,0.08))' }}>
        <Loader2 size={24} className="text-indigo-500 animate-spin" />
      </div>
      <p className="text-sm text-gray-400 font-medium">Loading analytics…</p>
    </div>
  )

  const d = data!

  return (
    <div className="space-y-5 pb-10">

      {/* ── Hero ── */}
      <div className="relative overflow-hidden rounded-3xl text-white"
        style={{ background: 'linear-gradient(135deg,#050d1f 0%,#0c1e3d 50%,#0d2a50 100%)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-12 -right-12 w-64 h-64 rounded-full"
            style={{ background: 'radial-gradient(circle,rgba(99,102,241,0.35) 0%,transparent 70%)', filter: 'blur(40px)' }} />
          <div className="absolute bottom-0 left-1/4 w-48 h-32 rounded-full"
            style={{ background: 'radial-gradient(circle,rgba(6,182,212,0.2) 0%,transparent 70%)', filter: 'blur(30px)' }} />
        </div>
        <div className="relative z-10 p-5 lg:p-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/10 rounded-full px-3 py-1 mb-3">
              <BarChart3 size={11} className="text-cyan-300" />
              <span className="text-[11px] text-blue-200 font-semibold uppercase tracking-wider">Analytics</span>
            </div>
            <h1 className="text-xl lg:text-2xl font-black">Reports &amp; Insights</h1>
            <p className="text-blue-300/70 text-sm mt-1.5">Live data from all {d.total} tasks</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {(['all', '30d', '7d'] as const).map(r => (
              <button key={r} onClick={() => setRange(r)}
                className={`text-[11px] font-black px-3.5 py-2 rounded-xl transition-all ${
                  range === r ? 'bg-white text-gray-900' : 'bg-white/10 text-blue-200 hover:bg-white/20'
                }`}>
                {r === 'all' ? 'All Time' : r === '30d' ? 'Last 30d' : 'Last 7d'}
              </button>
            ))}
            <button onClick={load}
              className="p-2 bg-white/10 rounded-xl hover:bg-white/20 transition-all" title="Refresh">
              <RefreshCw size={14} className={`text-blue-200 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* ── KPI cards ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Total Tasks" value={d.total} icon={BarChart3}
          grad="linear-gradient(135deg,#6366f1,#8b5cf6)" sub="All statuses" />
        <KpiCard label="Closed" value={d.byStatus['closed'] ?? 0} icon={CheckCircle2}
          grad="linear-gradient(135deg,#10b981,#059669)"
          sub={`${d.closureRate}% closure rate`} trend={d.closureRate >= 60 ? 'up' : 'down'} />
        <KpiCard label="Open / Assigned" value={d.openTasks} icon={Clock}
          grad="linear-gradient(135deg,#3b82f6,#2563eb)"
          sub="Awaiting action" trend={d.openTasks > 10 ? 'up' : 'flat'} />
        <KpiCard label="Pending Approval" value={d.pendingApproval} icon={AlertTriangle}
          grad="linear-gradient(135deg,#f59e0b,#d97706)"
          sub="Needs admin review" trend={d.pendingApproval > 0 ? 'up' : 'flat'} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Avg Rating" value={d.avgRating ? `${d.avgRating} ★` : '—'} icon={Star}
          grad="linear-gradient(135deg,#f59e0b,#d97706)"
          sub={`from ${d.ratingCount} reviews`} trend={d.avgRating >= 4 ? 'up' : d.avgRating ? 'down' : 'flat'} />
        <KpiCard label="Recommend %" value={d.ratingCount ? `${d.recommendPct}%` : '—'} icon={ThumbsUp}
          grad="linear-gradient(135deg,#a855f7,#8b5cf6)"
          sub="Would recommend BSNL" trend={d.recommendPct >= 70 ? 'up' : 'flat'} />
        <KpiCard label="Avg Close Time" value={d.avgCloseTimeDays != null ? `${d.avgCloseTimeDays}d` : '—'} icon={TrendingUp}
          grad="linear-gradient(135deg,#06b6d4,#0891b2)"
          sub="Days from open to close" trend={d.avgCloseTimeDays && d.avgCloseTimeDays < 3 ? 'up' : 'flat'} />
        <KpiCard label="Engineers" value={d.engineers.length || d.engineerStats.length} icon={Users}
          grad="linear-gradient(135deg,#64748b,#475569)"
          sub="Active field staff" />
      </div>

      {/* ── Status + Type donuts ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <SectionCard title="By Status" icon={Zap} iconBg="bg-indigo-500">
          <Donut segments={statusSegments} total={d.total} label="Task Status Breakdown" />
        </SectionCard>
        <SectionCard title="By Type" icon={BarChart3} iconBg="bg-blue-500">
          <Donut segments={typeSegments} total={d.total} label="Task Type Breakdown" />
        </SectionCard>
      </div>

      {/* ── Monthly trend ── */}
      <SectionCard title="Monthly Trend" subtitle="Tasks created vs closed (last 6 months)"
        icon={Calendar} iconBg="bg-violet-500">
        <div className="flex items-center gap-4 mb-3">
          <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: 'rgba(99,102,241,0.7)' }} /><span className="text-xs text-gray-500">Created</span></div>
          <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: 'rgba(16,185,129,0.7)' }} /><span className="text-xs text-gray-500">Closed</span></div>
        </div>
        <MonthlyBars data={d.monthly} />
      </SectionCard>

      {/* ── Priority breakdown ── */}
      <SectionCard title="Priority Breakdown" subtitle="Distribution across priority levels"
        icon={AlertTriangle} iconBg="bg-orange-500">
        {(['urgent', 'high', 'medium', 'low'] as TaskPriority[]).map(p => (
          <HBar key={p} label={p} value={d.byPriority[p] ?? 0} total={d.total}
            color={PRIORITY_COLOR[p]}
            badge={p === 'urgent' ? '🔴' : p === 'high' ? '🟠' : undefined} />
        ))}
      </SectionCard>

      {/* ── Engineer leaderboard ── */}
      {d.engineerStats.length > 0 && (
        <SectionCard title="Engineer Leaderboard" subtitle="Tasks closed and customer ratings per engineer"
          icon={Award} iconBg="bg-emerald-500">
          <div className="space-y-3">
            {d.engineerStats.slice(0, 8).map((e, i) => (
              <div key={e.id} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
                <span className={`w-6 h-6 rounded-xl flex items-center justify-center text-[10px] font-black shrink-0 ${
                  i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-gray-100 text-gray-600' : i === 2 ? 'bg-orange-50 text-orange-600' : 'bg-gray-50 text-gray-400'
                }`}>{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-800 truncate">{e.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-gray-400">{e.closed}/{e.total} closed</span>
                    {e.avgRating > 0 && <MiniStars rating={e.avgRating} />}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <span className={`text-sm font-black ${e.rate >= 70 ? 'text-emerald-600' : e.rate >= 40 ? 'text-amber-600' : 'text-gray-500'}`}>
                    {e.rate}%
                  </span>
                  <p className="text-[10px] text-gray-400">closure</p>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* ── City distribution ── */}
      {topCities.length > 0 && (
        <SectionCard title="By City / Zone" subtitle="Task volume per location"
          icon={MapPin} iconBg="bg-cyan-500">
          {topCities.map(([city, count]) => (
            <HBar key={city} label={city} value={count} total={d.total} color="#06b6d4" />
          ))}
        </SectionCard>
      )}

      {/* ── Customer satisfaction detail ── */}
      {d.ratingCount > 0 && (
        <SectionCard title="Customer Satisfaction" subtitle="Feedback breakdown from closed tasks"
          icon={Star} iconBg="bg-amber-500">
          <div className="grid grid-cols-3 gap-3 mb-5">
            {[
              { label: 'Avg Overall', value: `${d.avgRating} / 5`, color: 'text-amber-600' },
              { label: 'Reviews', value: d.ratingCount, color: 'text-gray-700' },
              { label: 'Recommend', value: `${d.recommendPct}%`, color: 'text-emerald-600' },
            ].map(m => (
              <div key={m.label} className="bg-gray-50 rounded-2xl px-3 py-3 text-center">
                <p className={`text-lg font-black ${m.color}`}>{m.value}</p>
                <p className="text-[9px] text-gray-400 font-bold mt-0.5">{m.label}</p>
              </div>
            ))}
          </div>
          {/* Per-category averages */}
          {d.tasks.some(t => t.feedbackCategories) && (() => {
            const cats = { serviceQuality: [] as number[], responseTime: [] as number[], communication: [] as number[], workmanship: [] as number[] }
            d.tasks.forEach(t => {
              if (!t.feedbackCategories) return
              Object.entries(t.feedbackCategories).forEach(([k, v]) => {
                if (v && k in cats) cats[k as keyof typeof cats].push(v)
              })
            })
            return (
              <div className="space-y-1">
                {([['serviceQuality','Service Quality'],['responseTime','Response Time'],['communication','Communication'],['workmanship','Workmanship']] as const).map(([k, label]) => {
                  const a = avg(cats[k])
                  return a > 0 ? (
                    <div key={k} className="flex items-center gap-3 py-1.5">
                      <span className="w-28 text-xs font-semibold text-gray-600 shrink-0">{label}</span>
                      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full bg-amber-400 transition-all duration-700"
                          style={{ width: `${(a / 5) * 100}%` }} />
                      </div>
                      <span className="w-8 text-right text-xs font-black text-gray-700 shrink-0">{a.toFixed(1)}</span>
                    </div>
                  ) : null
                })}
              </div>
            )
          })()}
        </SectionCard>
      )}

    </div>
  )
}

export default ReportsPage
